import { evolve, filter, includes, isEmpty, prop, uniqBy, values } from 'ramda';

import { commonKeys } from '@/lib/utils';
import {
  type BasePageParams,
  type PageType,
  getPageUrl,
  getUrlRegion,
  urlToRouteMapping,
} from '@/app/urls/url-mapping';
import type {
  AvailableCourseTrack,
  Campus,
  CourseTrack,
  Language,
  Region,
  UrlEntry,
} from '@/types';
import { type DatoCampus, type DatoCourse } from '@/lib/datocms';

type PdpOptions = {
  campuses: Array<Pick<DatoCampus, 'code' | 'name'>>;
  courses: Array<Pick<DatoCourse, 'code' | 'name'>>;
  pathMap: Record<string, UrlEntry>;
};

export type ExternalLinkCourse = {
  course: { code: AvailableCourseTrack };
  link: string;
};

const uniqByCode = uniqBy(prop('code'));

const matchCourseCampus = (
  route: string
): { course: CourseTrack; campus: Campus } =>
  route.match(/pdp\/(?<course>[a-z]{2})\/(?<campus>[a-z]+)/u)?.groups as {
    course: CourseTrack;
    campus: Campus;
  };

export const getPdpOptions = (
  region: Region,
  language: Language
): PdpOptions => {
  const urlRegion = getUrlRegion({ language, region });
  const internalRoutes = values(urlToRouteMapping[urlRegion]);
  const pdpRoutes = filter(includes('pdp'), internalRoutes);

  const pdpOptions = pdpRoutes.reduce<PdpOptions>(
    (acc, pdpRoute) => {
      const { course, campus } = matchCourseCampus(pdpRoute);
      return {
        courses: uniqByCode([
          ...acc.courses,
          { code: course, name: commonKeys[language][course] },
        ]),
        campuses: uniqByCode([
          ...acc.campuses,
          { code: campus, name: commonKeys[language][campus] },
        ]),
        pathMap: {
          ...acc.pathMap,
          [`${campus}${course}`]: getPageUrl(
            `pdp/${course}/${campus}` as PageType,
            { language, region }
          ) as string,
        },
      };
    },
    { campuses: [], courses: [], pathMap: {} }
  );

  return pdpOptions;
};

type ExternalLinkOptions = Omit<PdpOptions, 'campuses'>;

function getExternalLinkCourseOptions(
  externalLinkCourses: ExternalLinkCourse[],
  language: Language,
  campuses: Array<Pick<DatoCampus, 'code' | 'name'>>
): ExternalLinkOptions {
  const courses = externalLinkCourses.map(({ course }) => ({
    code: course.code,
    name: commonKeys[language][course.code],
  }));

  const pathMap: Record<string, UrlEntry> = {};
  campuses.forEach((campus) => {
    externalLinkCourses.forEach(({ course, link }) => {
      const pathKey = `${campus.code}${course.code}`;
      pathMap[pathKey] = { url: link, isExternal: true };
    });
  });

  return { courses, pathMap };
}

export function getCourseCampusOptions(
  { region, language }: Pick<BasePageParams, 'language' | 'region'>,
  externalLinkCourses: ExternalLinkCourse[] | undefined = []
): PdpOptions {
  const { courses, campuses, pathMap } = getPdpOptions(region, language);

  if (isEmpty(externalLinkCourses)) return { courses, campuses, pathMap };

  const externalLinkOptions = getExternalLinkCourseOptions(
    externalLinkCourses,
    language,
    campuses
  );

  type Courses = PdpOptions['courses'];
  type TransformCourses = (value: Courses) => Courses;

  return evolve<Record<'courses', TransformCourses>, PdpOptions>(
    { courses: uniqByCode },
    {
      courses: [...courses, ...externalLinkOptions.courses],
      campuses,
      pathMap: { ...pathMap, ...externalLinkOptions.pathMap },
    }
  );
}
